import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { Table } from '@/components/ui/Table'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, IconButton, Stack } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { districtsBigApi } from '../api/streetsApi'
import { DistrictBigModal } from '../components/DistrictBigModal'

const columnHelper = createColumnHelper()

export default function DistrictsBig() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    city: '',
    page: 1,
    page_size: 20,
    ordering: 'district_big',
  })

  const { data, isLoading } = useQuery({
    queryKey: ['districts-big', filters.search, filters.city, filters.page, filters.page_size, filters.ordering],
    queryFn: () => {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.search) params.search = filters.search
      if (filters.city) params.city = filters.city
      if (filters.ordering) params.ordering = filters.ordering
      return districtsBigApi.getAll(params)
    },
  })

  const totalCount = data?.meta?.pagination?.total_count || data?.meta?.count || 0
  const pageCount = data?.meta?.pagination ? Math.ceil(totalCount / filters.page_size) : 1

  const deleteMutation = useMutation({
    mutationFn: districtsBigApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts-big'] })
      toastService.success({
        title: t('common.success'),
        description: t('streets.districtBigDeleted'),
      })
      setDeleteConfirm(null)
    },
    onError: () => {
      toastService.error({
        title: t('common.error'),
        description: t('streets.deleteError'),
      })
    },
  })

  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      meta: { width: '80px' },
    }),
    columnHelper.accessor('district_big', {
      header: t('streets.districtBig'),
    }),
    columnHelper.accessor('city_name', {
      header: t('streets.city'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: (info) => (
        <HStack spacing={1}>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Edit"
            onClick={() => {
              setEditingItem(info.row.original)
              setIsModalOpen(true)
            }}
          >
            <FiEdit2 />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label="Delete"
            onClick={() => setDeleteConfirm(info.row.original)}
          >
            <FiTrash2 />
          </IconButton>
        </HStack>
      ),
      meta: { width: '120px' },
    }),
  ]

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
      </Box>
    )
  }

  return (
    <Stack gap={4}>
      <Heading size="lg">{t('streets.tabs.districtsBig')}</Heading>

      <HStack justify="space-between" gap={4}>
        <HStack flex="1" maxW="600px" gap={2}>
          <Box flex="1">
            <AutocompleteSelect
              placeholder={t('streets.selectCity')}
              onChange={(city) => updateFilters({ city: city?.id || '' })}
              fetchOptions={geolocateApi.getCitiesAutocomplete}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              isClearable
            />
          </Box>
        </HStack>
        <Button onClick={() => { setEditingItem(null); setIsModalOpen(true) }} leftIcon={<FiPlus />}>
          {t('streets.createDistrictBig')}
        </Button>
      </HStack>

      <Box bg="white" borderRadius="md" border="1px solid" borderColor="#DFE1E6">
        <Table
          data={data?.data || []}
          columns={columns}
          enablePagination
          enableSorting
          manualPagination={!!data?.meta?.pagination}
          manualSorting
          pageCount={pageCount}
          totalCount={totalCount}
          state={{
            pagination: {
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            },
            sorting: filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : [],
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </Box>

      <DistrictBigModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        districtBig={editingItem}
      />

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
        title={t('streets.deleteConfirmTitle')}
        message={t('streets.deleteConfirmMessage', { name: deleteConfirm?.district_big })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />
    </Stack>
  )
}
